import {SortingState} from '@tanstack/table-core';
import {create} from 'zustand';
import type CreatorGroupMembership from '@/types/creatorGroupMembership';
import {CreatorActionKey} from '@/views/creators/components/creator-action/CreatorAction';

const MULTI_LIST_SELECTION = false;
const INIT_STATE = {
  selectedLists: new Set<string>(),
  selectedCreators: new Map(),
  activeCreatorAction: null,
  activeListAction: null,
  activeListId: null,
  tableFilterValue: null,
  sorting: [],
};

export type ListActionKey =
  | 'add-list'
  | 'edit-list'
  | 'delete-list'
  | 'share-list';

export type ListCreatorsStateDefinition = {
  selectedLists: Set<string>;
  selectedCreators: Map<string, CreatorGroupMembership>;
  activeCreatorAction: CreatorActionKey | null;
  activeListAction: ListActionKey | null;
  activeListId: string | null;
  tableFilterValue: string | null;
  sorting: SortingState;
};

const listCreatorsStore = create<ListCreatorsStateDefinition>(() => INIT_STATE);

const getNewSelectedCreators = ({
  selectedLists,
  selectedCreators,
}: {
  selectedLists: ListCreatorsStateDefinition['selectedLists'];
  selectedCreators: ListCreatorsStateDefinition['selectedCreators'];
}) => {
  return new Map<string, CreatorGroupMembership>(
    [...selectedCreators].filter(([, membership]) =>
      membership.labels.some(({id}) => selectedLists.has(id))
    )
  );
};

const reset = () => {
  listCreatorsStore.setState(INIT_STATE);
};

const selectList = (listId: string) => {
  const {selectedLists, selectedCreators} = listCreatorsStore.getState();

  const newSelectedLists = MULTI_LIST_SELECTION
    ? new Set(selectedLists)
    : new Set<string>();

  newSelectedLists.add(listId);

  const newSelectedCreators = getNewSelectedCreators({
    selectedLists: newSelectedLists,
    selectedCreators,
  });

  listCreatorsStore.setState({
    selectedLists: newSelectedLists,
    selectedCreators: newSelectedCreators,
  });
};

const deselectList = (listId: string) => {
  const {selectedLists, selectedCreators} = listCreatorsStore.getState();

  const newSelectedLists = new Set(selectedLists);
  newSelectedLists.delete(listId);

  const newSelectedCreators = getNewSelectedCreators({
    selectedLists: newSelectedLists,
    selectedCreators,
  });

  listCreatorsStore.setState({
    selectedLists: newSelectedLists,
    selectedCreators:
      newSelectedLists.size === 0 ? selectedCreators : newSelectedCreators,
  });
};

const toggleList = (listId: string) => {
  if (listCreatorsStore.getState().selectedLists.has(listId)) {
    deselectList(listId);
  } else {
    selectList(listId);
  }
};

const selectCreator = (id: string, creator: CreatorGroupMembership) => {
  const newSelectedCreators = new Map<string, CreatorGroupMembership>(
    listCreatorsStore.getState().selectedCreators
  );
  newSelectedCreators.set(id, creator);
  listCreatorsStore.setState({selectedCreators: newSelectedCreators});
};

const deselectCreator = (creatorId: string) => {
  const newSelectedCreators = new Map<string, CreatorGroupMembership>(
    listCreatorsStore.getState().selectedCreators
  );
  newSelectedCreators.delete(creatorId);
  listCreatorsStore.setState({selectedCreators: newSelectedCreators});
};

const toggleCreator = (id: string, creator: CreatorGroupMembership) => {
  if (listCreatorsStore.getState().selectedCreators.has(id)) {
    deselectCreator(id);
  } else {
    selectCreator(id, creator);
  }
};

const selectCreators = (creators: CreatorGroupMembership[]) => {
  const newSelectedCreators = new Map<string, CreatorGroupMembership>(
    listCreatorsStore.getState().selectedCreators
  );
  creators.forEach((creator) => newSelectedCreators.set(creator.id, creator));
  listCreatorsStore.setState({selectedCreators: newSelectedCreators});
};

const deselectAllCreators = () => {
  listCreatorsStore.setState({selectedCreators: new Map()});
};

const showAllCreators = () => {
  deselectAllLists();
  setTableFilterValue(null);
};

const selectMultipleLists = (listIds: string[]) => {
  const newSelectedLists = new Set<string>(
    listCreatorsStore.getState().selectedLists
  );
  listIds.forEach((listId) => newSelectedLists.add(listId));
  listCreatorsStore.setState({selectedLists: newSelectedLists});
};

const deselectAllLists = () => {
  listCreatorsStore.setState({selectedLists: new Set()});
};

const setTableFilterValue = (value: string | null) => {
  listCreatorsStore.setState({
    tableFilterValue: value,
  });
};

const clearTableFilterValue = () => {
  setTableFilterValue(null);
};

const setActiveCreatorAction = (creatorAction: CreatorActionKey | null) => {
  listCreatorsStore.setState({activeCreatorAction: creatorAction});
};

const setActiveListAction = (listAction: ListActionKey | null, id?: string) => {
  if (id) setActiveListId(id);
  listCreatorsStore.setState({activeListAction: listAction});
};

const setActiveListId = (listId: string | null) => {
  listCreatorsStore.setState({activeListId: listId});
};

const setSorting = (sorting: SortingState) => {
  listCreatorsStore.setState({sorting});
};

export const listCreatorsActions = {
  reset,
  selectList,
  deselectList,
  toggleList,
  selectMultipleLists,
  deselectAllLists,
  selectCreator,
  deselectCreator,
  toggleCreator,
  selectCreators,
  deselectAllCreators,
  setTableFilterValue,
  clearTableFilterValue,
  setActiveCreatorAction,
  setActiveListAction,
  setActiveListId,
  showAllCreators,
  setSorting,
};

export default listCreatorsStore;
